/**
 * Znaczniki fazy, które wysyła `GenerateReportJob` w polu `message`.
 * Muszą się zgadzać z `GenerateReportJob.PhaseCalculating/PhaseComposing`.
 */
export const TASK_PHASE_CALCULATING = 'phase:calculating'
export const TASK_PHASE_COMPOSING = 'phase:composing'

export type TaskProgressView = {
  /** Faza: liczenie danych (bez licznika) albo składanie dokumentu (z licznikiem). */
  phase: 'calculating' | 'composing' | 'unknown'
  /** Gotowy sufiks do doklejenia po nazwie operacji. */
  suffix: string
}

const formatElapsed = (startedAt?: string): string => {
  if (!startedAt) return ''
  const started = Date.parse(startedAt)
  if (Number.isNaN(started)) return ''
  const seconds = Math.max(0, Math.floor((Date.now() - started) / 1000))
  const mm = Math.floor(seconds / 60)
  const ss = seconds % 60
  return `${mm}:${String(ss).padStart(2, '0')}`
}

/**
 * Opis postępu zadania.
 *
 * Sam licznik nie wystarcza: dla raportów typu `DriverDayDetails` faza liczenia danych
 * trwa minuty, a licznik stoi przez cały ten czas na `0/267`, bo kierowcy przeliczają
 * się dopiero przy składaniu dokumentu. Z perspektywy użytkownika wyglądało to
 * identycznie jak zawieszenie. Dlatego w fazie liczenia pokazujemy nazwę etapu
 * i upływający czas — coś, co się rusza — a licznik dopiero wtedy, gdy naprawdę tyka.
 */
export const describeTaskProgress = (
  task:
    | {
        message?: string
        progressCurrent?: number
        progressTotal?: number
        createdAt?: string
      }
    | null
    | undefined,
  t: (key: string) => string,
): TaskProgressView => {
  if (!task) return { phase: 'unknown', suffix: '' }

  const current = task.progressCurrent ?? 0
  const total = task.progressTotal ?? 0
  const isCalculating =
    task.message === TASK_PHASE_CALCULATING ||
    // Zapas dla zadań sprzed tej zmiany i dla typów, które fazy nie wysyłają:
    // zerowy licznik przy znanym całkowitym to zawsze etap przed składaniem.
    (!task.message && total > 0 && current === 0)

  if (isCalculating) {
    const elapsed = formatElapsed(task.createdAt)
    return {
      phase: 'calculating',
      suffix: elapsed
        ? ` ${t('printout.phase-calculating')} (${elapsed})`
        : ` ${t('printout.phase-calculating')}`,
    }
  }

  if (total > 0) {
    return { phase: 'composing', suffix: ` ${current}/${total}` }
  }

  return { phase: 'unknown', suffix: '' }
}
